import { z } from 'zod'
import { auditFieldsSchema, decimalSchema, uuidSchema } from './common.ts'
import { dealFiltersSchema, dealStageSchema } from './deals.ts'
import { DEAL_BOARD_SORTS } from './view-preferences.ts'

export const SAVED_VIEW_TYPES = ['kanban', 'list'] as const
export const SAVED_VIEW_BUILT_IN_FIELD_KEYS = [
  'name',
  'clientName',
  'clientEmail',
  'clientPhone',
  'stage',
  'amount',
  'managerName',
  'nextAction',
  'nextActionAt',
  'expectedCloseAt',
  'aiScore',
  'orderNumber',
  'lostReason',
  'stageChangedAt',
  'createdAt',
  'updatedAt',
] as const

export const savedViewTypeSchema = z.enum(SAVED_VIEW_TYPES)
export type SavedViewType = z.infer<typeof savedViewTypeSchema>

/**
 * List grouping. Deliberately a closed set rather than "any field": grouping by amount or by a
 * date produces one group per row, which looks like a broken table rather than a grouped one.
 */
export const SAVED_VIEW_GROUP_BYS = ['none', 'stage', 'manager', 'client'] as const

export const savedViewGroupBySchema = z.enum(SAVED_VIEW_GROUP_BYS)
export type SavedViewGroupBy = z.infer<typeof savedViewGroupBySchema>

export const SAVED_VIEW_GROUP_BY_LABELS: Record<SavedViewGroupBy, string> = {
  none: 'Без групування',
  stage: 'За статусом',
  manager: 'За менеджером',
  client: 'За компанією',
}

const visibleFieldKeySchema = z.string().trim().min(1).max(64)

export const savedViewConfigSchema = z
  .strictObject({
    filters: dealFiltersSchema.default({}),
    visibleFields: z.strictObject({
      kanban: z.array(visibleFieldKeySchema),
      list: z.array(visibleFieldKeySchema),
    }),
    sort: z.enum(DEAL_BOARD_SORTS),
    /* Defaulted rather than required so every saved view stored before grouping existed still
       parses. A jsonb column has no migration to add a key to. */
    groupBy: savedViewGroupBySchema.default('none'),
    hiddenStages: z.array(dealStageSchema),
    showLost: z.boolean(),
  })
  .superRefine((config, context) => {
    if (config.filters.showLost !== undefined && config.filters.showLost !== config.showLost) {
      context.addIssue({
        code: 'custom',
        path: ['filters', 'showLost'],
        message: 'filters.showLost must match showLost',
      })
    }
  })

export type SavedViewConfig = z.infer<typeof savedViewConfigSchema>

export const buildSavedViewConfigSchema = (customFieldKeys: readonly string[]) => {
  const allowedKeys = new Set<string>([...SAVED_VIEW_BUILT_IN_FIELD_KEYS, ...customFieldKeys])
  return savedViewConfigSchema.superRefine((config, context) => {
    for (const [viewType, keys] of Object.entries(config.visibleFields)) {
      for (const [index, key] of keys.entries()) {
        if (allowedKeys.has(key)) continue
        context.addIssue({
          code: 'custom',
          path: ['visibleFields', viewType, index],
          message: `Unknown field key: ${key}`,
        })
      }
    }
  })
}

export const savedViewSchema = z
  .strictObject({
    id: uuidSchema,
    name: z.string().min(1),
    ownerId: uuidSchema,
    isShared: z.boolean(),
    viewType: savedViewTypeSchema,
    config: savedViewConfigSchema,
    position: decimalSchema,
  })
  .extend(auditFieldsSchema.shape)

export type SavedView = z.infer<typeof savedViewSchema>

const savedViewWriteFields = {
  name: z.string().trim().min(1).max(100),
  isShared: z.boolean(),
  viewType: savedViewTypeSchema,
  config: savedViewConfigSchema,
  position: decimalSchema,
}

export const createSavedViewInputSchema = z.strictObject({
  ...savedViewWriteFields,
  isShared: savedViewWriteFields.isShared.default(false),
  position: savedViewWriteFields.position.optional(),
})

export type CreateSavedViewInput = z.infer<typeof createSavedViewInputSchema>

export const updateSavedViewInputSchema = z
  .strictObject(savedViewWriteFields)
  .partial()
  .refine((input) => Object.keys(input).length > 0, 'At least one field must be updated')

export type UpdateSavedViewInput = z.infer<typeof updateSavedViewInputSchema>
